/**
 * PORTED — RO-3-00f / #44473.
 * Origin: `excellent/app/projects/_sections/section-audit-panel.tsx`.
 *
 * Cause A rewrite: the read half moves to `sectionAudit.panel`. Writes reuse
 * `otherActions.auditRun` / `auditFixFinding` / `auditDismissFinding` from
 * RO-2-11b. Live Next call sites stay on their server actions (PRD D6).
 */

import {
  SectionAuditPanel as SectionAuditPanelUi,
  SectionAuditCard as SectionAuditCardUi,
  type AuditFindingVm,
} from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import { sectionAudit } from "@excellent/ui/api/contracts/section-audit";
import { otherActions } from "@excellent/ui/api/contracts/other-actions";

export interface SectionAuditPanelProps {
  sectionSlug: string;
  workspaceId: number;
  projectId: number | null;
  projectSlug: string | null;
  display?: "panel" | "card";
}

export function SectionAuditPanel({
  sectionSlug,
  workspaceId,
  projectId,
  projectSlug,
  display = "panel",
}: SectionAuditPanelProps) {
  const { data, error, isLoading, mutate } = useApi(sectionAudit.panel, {
    query: {
      sectionSlug,
      workspaceId,
      projectId: projectId ?? undefined,
      projectSlug: projectSlug ?? undefined,
    },
  });

  if (isLoading && !data) return null;
  if (error || !data || !data.visible) return null;

  const findingsVm: AuditFindingVm[] = data.findings.map((f) => ({
    id: f.id,
    severity: f.severity,
    title: f.title,
    detail: f.detail,
    fix_prompt: f.fix_prompt,
    status: f.status as AuditFindingVm["status"],
  }));

  async function onRunAudit(): Promise<void> {
    const res = await apiCall(otherActions.auditRun, {
      body: {
        sectionSlug,
        projectId: projectId ?? null,
        projectSlug: projectSlug ?? null,
      },
    });
    if (!res.ok) throw new Error(res.error ?? "Audit failed to start");
    await mutate();
  }

  async function onFix(findingId: number): Promise<void> {
    const res = await apiCall(otherActions.auditFixFinding, {
      body: { findingId, projectSlug: projectSlug ?? null },
    });
    if (!res.ok) throw new Error(res.error ?? "Fix failed to start");
    await mutate();
  }

  async function onDismiss(findingId: number): Promise<void> {
    await apiCall(otherActions.auditDismissFinding, {
      body: { findingId, projectSlug: projectSlug ?? null },
    });
    await mutate();
  }

  if (display === "card") {
    return (
      <SectionAuditCardUi
        sectionSlug={sectionSlug}
        sectionLabel={data.sectionLabel ?? sectionSlug}
        focus={data.focus ?? ""}
        score={data.score}
        ageDays={data.ageDays}
        cadenceDays={data.cadenceDays ?? 30}
        isDue={data.isDue}
        running={data.running}
        lastRunStatus={
          (data.lastRunStatus as
            | "running"
            | "completed"
            | "error"
            | "timeout"
            | "superseded"
            | null) ?? null
        }
        findings={findingsVm}
        href={`/projects/sections/${sectionSlug}/audit`}
        onRunAudit={onRunAudit}
        onFix={onFix}
        onDismiss={onDismiss}
      />
    );
  }

  return (
    <SectionAuditPanelUi
      sectionLabel={data.sectionLabel ?? sectionSlug}
      focus={data.focus ?? ""}
      score={data.score}
      ageDays={data.ageDays}
      cadenceDays={data.cadenceDays ?? 30}
      isDue={data.isDue}
      running={data.running}
      lastRunStatus={
        (data.lastRunStatus as
          | "running"
          | "completed"
          | "error"
          | "timeout"
          | "superseded"
          | null) ?? null
      }
      findings={findingsVm}
      onRunAudit={onRunAudit}
      onFix={onFix}
      onDismiss={onDismiss}
    />
  );
}
